import { AxiosRequestConfig } from "axios";
import { IFundOrderTableSearchParams } from "../Interfaces/manualInterfaces";
import { ISearchParameters, Report } from "../Interfaces/TableInterfaces";
import { Packet } from "../Interfaces/commonDtos";
import {
  DropdownItem,
  OrderFilterOptions,
} from "../Interfaces/dropdownInterfaces";
import {
  ErrorTableReport,
  FileOrderTableReport,
  IFailedOrderBulkPreviewResponse,
  IFailedOrderResendResult,
  IFailedOrderTableReport,
  OrderRow,
  OrderTableSearchParam,
  ShekarChanceReport,
} from "../Interfaces/orderInterfaces";
import {
  Graph_Dto,
  IOrderBookGraphSearchParams,
} from "../Interfaces/orderbookGraphInterfaces";
import {
  IBrokerOrdersSearchParam,
  IEditStepOrderFormValues,
  IFailedStepOrderEditItem,
  IStepEditVolumeContext,
  IStepEditVolumePreview,
  VolumeCalculationType,
  IGetPortfoTableResponse,
  IGetStepOrderBookResponse,
  IStepAddOrderFormValues,
  IStepFundOrderTableReport,
  IStepFundSellSettings,
  IStepAggregationTableReport,
  IStepChanceSearchParams,
  IStepCompetitionChanceSearchParam,
  IStepCompetitionConfigFormValues,
  IStepCompetitionConfigSearchParam,
  IStepCompetitionConfigTableReport,
  IStepOptionDropdownItem,
  IStepOrderTableReport,
  IStepOrderPausePlayStatus,
  IStepSubOrdersTableReport,
  IStepTablesSearchParam,
  IStepTradeTableReport,
  StepInstrumentType,
  IStepOrderPreview,
  IOrderFileUploadResponse,
  IStepOrderStateChangeLog,
} from "../Interfaces/stepInterfaces";
import {
  deleteRequest,
  getRequest,
  getTable,
  patchRequest,
  postDownloadExcel,
  postRequest,
  putRequest,
} from "./requestTemplates";

export const uploadStepOrderFile = (
  searchParam: FormData | null,
): Promise<IOrderFileUploadResponse<FileOrderTableReport>> =>
  postRequest<FormData | null, IOrderFileUploadResponse<FileOrderTableReport>>(
    "/v1/step/uploadOrderFile",
    searchParam,
  );

// Unwrapped Report<T> shape for the generic multi-type UploadAll flow, which has no concept
// of the per-direction/symbol volume summary shown in the Step page's own bulk-upload preview.
export const uploadStepOrderFileReport = (
  searchParam: FormData | null,
): Promise<Report<FileOrderTableReport>> =>
  uploadStepOrderFile(searchParam).then((resp) => resp.report);

export const ORDER_BOOK_POLL_INTERVAL_MS = 10_000;

export const getStepOrderBook = (
  insCode?: string,
  instrumentType?: StepInstrumentType | null,
): Promise<IGetStepOrderBookResponse> => {
  const urlSearchParam = new URLSearchParams();
  if (insCode) urlSearchParam.append("InstrumentId", insCode.toString());
  if (instrumentType) urlSearchParam.append("type", instrumentType.toString());

  let urlStr = "/v1/Step/OrderBook";
  if (insCode || instrumentType) urlStr += "?" + urlSearchParam.toString();

  return getRequest(urlStr);
};

export const stepBulkOrderCancel = (
  rowIds: number[],
): Promise<Packet<boolean>> => deleteRequest("/v1/step/bulkcancel", rowIds);

export const getStepOrderFilterOptions = (
  searchParam: IStepTablesSearchParam,
): Promise<OrderFilterOptions> =>
  postRequest("/v1/step/orderFilterOptions", searchParam);

export const getStepOrderTable = (
  searchParam: IStepTablesSearchParam,
  offset: number | null,
  pageSize: number | null,
  sortingType: string,
  additional?: DropdownItem,
  axiosOptions?: AxiosRequestConfig,
): Promise<Report<IStepOrderTableReport>> =>
  getTable<IStepTablesSearchParam, IStepOrderTableReport>(
    "/v1/step/ordertable",
    searchParam,
    offset,
    pageSize,
    sortingType,
    additional,
    axiosOptions,
  );

export const getStepOrderPausePlayStatus = (
  orderIds: number[],
  signal: AbortSignal,
): Promise<IStepOrderPausePlayStatus[]> =>
  postRequest<number[], IStepOrderPausePlayStatus[]>(
    "/v1/step/pausePlayStatus",
    orderIds,
    { signal },
  );

export const resumeStepOrder = (orderId: number): Promise<Packet<boolean>> =>
  putRequest(`/v1/step/resume/${orderId}`);

export const pauseStepOrder = (orderId: number): Promise<Packet<boolean>> =>
  putRequest(`/v1/step/pause/${orderId}`);

export const getStepOrderStateChangeHistory = (
  orderId: number,
): Promise<IStepOrderStateChangeLog[]> =>
  getRequest(`/v1/step/stateChangeHistory/${orderId}`);

export const bulkResumeStepOrder = (
  selectedRowsIds: number[],
  signal?: AbortSignal,
): Promise<Packet<boolean>> =>
  putRequest<number[], Packet<boolean>>(
    `/v1/step/bulkresume`,
    selectedRowsIds,
    signal ? { signal } : undefined,
  );

export const bulkPauseStepOrder = (
  selectedRowsIds: number[],
  signal?: AbortSignal,
): Promise<Packet<boolean>> =>
  putRequest<number[], Packet<boolean>>(
    `/v1/step/bulkpause`,
    selectedRowsIds,
    signal ? { signal } : undefined,
  );

export const getStepErrorsTable = <T extends ISearchParameters>(
  searchParam: T,
  offset: number | null,
  pageSize: number | null,
): Promise<Report<IStepOrderTableReport>> =>
  getTable<T, IStepOrderTableReport>(
    "/v1/step/errortable",
    searchParam,
    offset,
    pageSize,
  );

export const getStepErrorFilterOptions = (
  searchParam: IStepTablesSearchParam,
): Promise<OrderFilterOptions> =>
  postRequest("/v1/step/errorFilterOptions", searchParam);

export const getStepFailedOrderFilterOptions = (
  searchParam: IStepTablesSearchParam,
): Promise<OrderFilterOptions> =>
  postRequest("/v1/step/failedOrderFilterOptions", searchParam);

export const getStepFailedOrderTable = <T extends ISearchParameters>(
  searchParam: T,
  offset: number | null,
  pageSize: number | null,
): Promise<Report<IFailedOrderTableReport>> =>
  getTable<T, IFailedOrderTableReport>(
    "/v1/step/failedOrderTable",
    searchParam,
    offset,
    pageSize,
  );

export const previewStepFailedOrderResend = (id: number): Promise<IStepOrderPreview> =>
  postRequest("/v1/step/previewFailedOrderResend", { id });

export const previewStepFailedOrderResendBulk = (
  ids: number[],
): Promise<IFailedOrderBulkPreviewResponse<IStepOrderPreview>> =>
  postRequest("/v1/step/previewFailedOrderResendBulk", { ids });

export const resendStepFailedOrdersBulk = (
  items: Array<{ id: number; orderDto: any }>,
): Promise<IFailedOrderResendResult[]> =>
  postRequest("/v1/step/resendFailedOrdersBulk", items);

export const resendStepFailedOrder = (
  id: number,
  orderDto: any,
): Promise<Packet<boolean>> =>
  postRequest("/v1/step/resendFailedOrder", { id, orderDto });

export const refreshStepOrder = (id: number): Promise<Packet<boolean>> =>
  getRequest(`/v1/step/updateUserOrders?userId=${id}`);

export const cancelStepOrder = (orderId: number): Promise<Packet<boolean>> =>
  deleteRequest(`/v1/step/cancel/${orderId}`);

export const getStepShekarChanceTable = (
  searchParam: IStepChanceSearchParams,
  offset: number | null,
  pageSize: number | null,
): Promise<Report<ShekarChanceReport>> =>
  getTable<IStepChanceSearchParams, ShekarChanceReport>(
    "/v1/step/shekarChanceTable",
    searchParam,
    offset,
    pageSize,
  );

export const getStepTradeFilterOptions = (
  searchParam: IStepTablesSearchParam,
): Promise<OrderFilterOptions> =>
  postRequest("/v1/step/tradeFilterOptions", searchParam);

export const getStepTradeTable = (
  searchParam: IStepTablesSearchParam,
  offset: number | null,
  pageSize: number | null,
  sortingType: string,
  additional?: DropdownItem,
  axiosOptions?: AxiosRequestConfig,
): Promise<Report<IStepTradeTableReport>> =>
  getTable<IStepTablesSearchParam, IStepTradeTableReport>(
    "/v1/step/tradetable",
    searchParam,
    offset,
    pageSize,
    sortingType,
    additional,
    axiosOptions,
  );

export const getStepAggregatedTradeTable = (
  searchParam: IStepTablesSearchParam,
  offset: number | null,
  pageSize: number | null,
  sortingType: string,
  additional?: DropdownItem,
  axiosOptions?: AxiosRequestConfig,
): Promise<Report<IStepTradeTableReport>> =>
  getTable<IStepTablesSearchParam, IStepTradeTableReport>(
    "/v1/step/tradeaggregatetable",
    searchParam,
    offset,
    pageSize,
    sortingType,
    additional,
    axiosOptions,
  );

export const getPortfo = (
  userId: number | null,
): Promise<IGetPortfoTableResponse> => {
  let urlStr = "/v1/Step/Portfo";
  const urlSearchParam = new URLSearchParams();
  if (userId) {
    urlStr += "?";
    urlSearchParam.append("userId", userId.toString());
  }

  return getRequest(urlStr);
};

export const getStepInsTypes = (): Promise<DropdownItem[]> =>
  getRequest("/v1/Step/GetStepTypes");

export const getStepInstruments = (): Promise<IStepOptionDropdownItem[]> =>
  getRequest("v1/step/getInstrumentOptions");

export const getCompetitionInstrumentOptions = (): Promise<IStepOptionDropdownItem[]> =>
  getRequest("/v1/step/getCompetitionInstrumentOptions");

export const getStepInstrumentOptionPair = (
  insId: string,
): Promise<IStepOptionDropdownItem> =>
  getRequest(`v1/step/getInstrumentOptionPair?insId=${insId}`);

export const addStepShekarOrder = (
  formValues: IStepAddOrderFormValues,
): Promise<Packet<boolean>> =>
  postRequest("/v1/step/addShekarOrder", formValues);

export const getSubOrders = (
  searchParam: { stepOrderId: number },
  offset: number | null,
  pageSize: number | null,
): Promise<Report<IStepSubOrdersTableReport>> =>
  getTable<{ stepOrderId: number }, IStepSubOrdersTableReport>(
    "/v1/Step/GetSuborders",
    searchParam,
    offset,
    pageSize,
  );

export const getSubOrderChances = (
  searchParam: { shekarOrderId: number },
  offset: number | null,
  pageSize: number | null,
): Promise<Report<IStepOrderTableReport>> =>
  getTable<{ shekarOrderId: number }, IStepOrderTableReport>(
    "/v1/step/shekarChanceTable",
    searchParam,
    offset,
    pageSize,
  );
export const getCompetitionSubOrderChances = (
  searchParam: { orderId: number },
  offset: number | null,
  pageSize: number | null,
): Promise<Report<IStepOrderTableReport>> =>
  getTable<{ orderId: number }, IStepOrderTableReport>(
    "/v1/step/competitionChanceTable",
    searchParam,
    offset,
    pageSize,
  );

export const getBrokerOrdersTable = (
  searchParam: IBrokerOrdersSearchParam,
  offset: number | null,
  pageSize: number | null,
): Promise<Report<IStepOrderTableReport>> =>
  getTable<IBrokerOrdersSearchParam, IStepOrderTableReport>(
    "/v1/step/getBrokerOrders",
    searchParam,
    offset,
    pageSize,
  );

export const addStepBulkOrder = (
  orderRows: Array<OrderRow> | null,
): Promise<Report<ErrorTableReport>> =>
  postRequest("/v1/step/addBulkOrder", orderRows);

export const editStepOrder = (
  formValues: IEditStepOrderFormValues,
): Promise<Packet<boolean> & {
  summary?: {
    editedCount: number;
    reducedVolumeCount?: number;
    skippedUnresolvedCount: number;
    noRemainingVolumeCount: number;
  } | null;
  failedOrders?: IFailedStepOrderEditItem[];
}> => patchRequest("/v1/step/editorder", formValues);

export const getStepEditVolumeContext = (stepOrderId: number): Promise<IStepEditVolumeContext> =>
  getRequest(`/v1/step/editVolumeContext?stepOrderId=${stepOrderId}`);

export const previewStepEditVolume = (body: {
  stepOrderId: number;
  volumeCalculationType: VolumeCalculationType;
  volumeCalculationValue: number;
  fromPrice: number;
  toPrice: number;
}): Promise<IStepEditVolumePreview> => postRequest("/v1/step/previewEditVolume", body);

export const getSimilarStepOrders = (
  stepOrderId: number,
): Promise<DropdownItem[]> =>
  getRequest(`/v1/step/getSimilarOrders?stepOrderId=${stepOrderId}`);

export const cancelStepBrokerOrder = (
  orderId: number,
): Promise<Packet<boolean>> =>
  deleteRequest("/v1/step/deleteBrokerOrder", { orderId });

export const getStepGraph = async (
  body: IOrderBookGraphSearchParams,
): Promise<Array<Graph_Dto>> => postRequest("/v1/step/graph", body);

export const getStepTradeExcelFile = (searchParam: IStepTablesSearchParam) => {
  const rand = Math.random();
  return postDownloadExcel(`/v1/step/tradeExcel?${rand}`, searchParam);
};

export const getStepTradeAggregateExcelFile = (
  searchParam: IStepTablesSearchParam,
) => {
  const rand = Math.random();
  return postDownloadExcel(`/v1/step/tradeAggregateExcel?${rand}`, searchParam);
};

export const addStepCompetitionOrder = (
  formValues: IStepAddOrderFormValues,
): Promise<Packet<boolean>> =>
  postRequest("/v1/step/addCompetitionOrder", formValues);

export const addOrEditCompetitionConfig = (
  formValues: IStepCompetitionConfigFormValues,
): Promise<Packet<boolean>> =>
  postRequest("/v1/step/addOrEditCompetitionConfig", formValues);

export const deleteCompetitionConfig = (
  insId: string,
): Promise<Packet<boolean>> =>
  deleteRequest(`/v1/step/deleteCompetitionConfig?insId=${insId}`);

export const getCompetitionBlockSizePercent = (): Promise<Packet<number>> =>
  getRequest("/v1/step/getCompetitionBlockSizePercent");

export const setCompetitionBlockSizePercent = (
  blockSizePercent: number,
): Promise<Packet<boolean>> =>
  postRequest("/v1/step/setCompetitionBlockSizePercent", { blockSizePercent });

export const getStepCompetitionConfigTable = (
  searchParam: IStepCompetitionConfigSearchParam,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<IStepCompetitionConfigTableReport>> =>
  getTable<IStepCompetitionConfigSearchParam, IStepCompetitionConfigTableReport>(
    "/v1/step/competitionConfigTable",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

export const getStepAggregationTable = (
  searchParam: IStepTablesSearchParam,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
  additional?: DropdownItem,
  axiosOptions?: AxiosRequestConfig,
): Promise<Report<IStepAggregationTableReport>> =>
  getTable<IStepTablesSearchParam, IStepAggregationTableReport>(
    "/v1/step/aggregateOrderTableByInstrument",
    searchParam,
    offset,
    pageSize,
    sortingType,
    additional,
    axiosOptions,
  );

export const getStepCompetitionChanceTable = (
  searchParam: IStepCompetitionChanceSearchParam,
  offset: number | null,
  pageSize: number | null,
): Promise<Report<any>> =>
  getTable<IStepCompetitionChanceSearchParam, any>(
    "/v1/step/shekarCompetitionChanceTable",
    searchParam,
    offset,
    pageSize,
  );

export const getPreview = (
  formValues: IStepAddOrderFormValues,
): Promise<IStepOrderPreview> =>
  postRequest("/v1/step/previewStepOrder", formValues);

export const getStepFundOrderFilterOptions = (
  searchParam: IFundOrderTableSearchParams,
): Promise<OrderFilterOptions> =>
  postRequest("/v1/step/fundOrderFilterOptions", searchParam);

export const getStepFundOrderTable = (
  searchParam: IFundOrderTableSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<IStepFundOrderTableReport>> =>
  getTable<IFundOrderTableSearchParams, IStepFundOrderTableReport>(
    "/v1/step/fundOrderTable",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

export const getStepFundSellSettings = (): Promise<IStepFundSellSettings> =>
  getRequest("/v1/step/fundSellSettings");

export const setStepFundSellSettings = (
  settings: IStepFundSellSettings,
): Promise<Packet<boolean>> =>
  postRequest("/v1/step/fundSellSettings", settings);

export const getStepAggregationFilterOptions = (
  searchParam: IStepTablesSearchParam,
): Promise<OrderFilterOptions> =>
  postRequest("/v1/step/aggregationFilterOptions", searchParam);
